import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
  Typography,
  Box,
  FormControlLabel,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  Button,
  LinearProgress,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@material-ui/core';

import {
  selectCurrentCountry,
} from '../countries/countriesSlice';

import { selectCatalogGlobals } from '../app/appSlice';

import TopSection from '../common/topSection';
import { Select } from '../common/fields';
import Alert from '../common/alert';

import { API } from '../../services/api';
import useTitle from '../../hooks/use-title.hook';
const api = new API();

const CatalogPage = props => {
  const { t } = useTranslation();
  const types = useSelector(selectCatalogGlobals).types;
  const typeOptions = types.map(type => ({label: type.name, value: type.id}));
  const currentCountry = useSelector(selectCurrentCountry); // Get default language from current country
  const { itemOwnerId } = useParams();

  const [status, setStatus] = useState('idle'); // idle, processing, error, complete
  const [errorMessage, setErrorMessage] = useState(null);
  const [progress, setProgress] = useState(null);
  const [jobId, setJobId] = useState(null);
  const [file, setFile] = useState(null);
  const timer = useRef();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [typeLanguages, setTypeLanguages] = useState(types?.[0]?.languages || []);

  useTitle(t('catalog:catalog'));

  // Change type specific languages when type changes
  const handleTypeChange = (event) => {
    const type = types.find(t => t.id === event.target.value);
    setTypeLanguages(type.languages);
  };

  /**
   * Queries the server for task status and progress
   *
   * @param {String} jobid - Job id
   */
  const poll = (jobid) => {
    const interval = 3000;
    console.log('polling', jobid);

    if(!jobid) {
      return;
    }

    api.getCatalog(jobid)
      .then(response => {
        console.log('poll response', response);
        // if operation successful:
        switch(response.result.status) {
          case 'done':
            setProgress(100);
            setStatus('complete');
            setFile(response.result.retdata.file);
            break;
          case 'running':
            setProgress(response.result.retdata.progress);
            timer.current = setTimeout(poll, interval, jobid);
            break;
          case 'failed':
            setStatus('error');
            setErrorMessage(response.result.retdata.error || 'ERROR'); //TODO localise generic
            setProgress(null);
            setJobId(null);
            break;
          default:
            // basically 'new'
            timer.current = setTimeout(poll, interval, jobid);
        }
      })
      .catch(err => {
        console.error('poll failed', err);
        setStatus('error');
        setErrorMessage(err.message);
      });
  }

  const onFormSubmit = (event) => {
    event.preventDefault();

    if(status === 'processing') {
      return false;
    }

    const form = event.target;
    const data = new FormData(form);

    const queryParams = {};
    for (const value of data.entries()) {
      queryParams[value[0]] = value[1];
    }

    console.log('form submit', queryParams);

    setDialogOpen(true);
    setStatus('processing');
    setErrorMessage(null);
    setProgress(null);
    setJobId(null);

    api.requestCatalogGeneration(queryParams).then(res => {
      console.log('requestCatalogGeneration', res);

      if(res.status === 'ok') {
        setJobId(res.result.jobId);
        setProgress(0);
        poll(res.result.jobId);
      } else {
        setStatus('error');

        let errorMessage = '';
        if(res.messages) {
          // Generic error message
          errorMessage = `${res.messages.join(', ')}. `;
        }

        if(res.result.errors) {
          // Api param validation errors
          Object.keys(res.result.errors).forEach(key => {
            errorMessage += `${key}: ${res.result.errors[key].join(', ')}. `;
          });
        }

        if(res.result.error && res.result.error.message) {
          errorMessage += res.result.error.message;
        }

        setErrorMessage(errorMessage);
      }
    }).catch(err => {
      console.error('requestCatalogGeneration failed', err);
      setStatus('error');

      setErrorMessage(err.message);
    });
  }

  const handleDialogClose = (event, reason) => {
    if (reason !== 'backdropClick') {
      console.log('handleDialogClose');
      clearTimeout(timer.current);
      api.cancelRequest();
      setDialogOpen(false);
      setStatus('idle');
      setErrorMessage(null);
      setProgress(null);
      setJobId(null);
    }
  };

  return (
    <>
      <TopSection>
        <Typography variant="h4" component="h1">{t('catalog:catalog')}</Typography>
      </TopSection>
      <Box p={3.5}>
        <Grid container>
          <Grid item xs={12} md={6}>
            <form method="get" onSubmit={onFormSubmit}>
              <input type="hidden" name="ownerId" value={itemOwnerId} />
              <Typography variant="h6" component="h2">{t('catalog:generateProductCatalog')}</Typography>
              <Box py={1}>
                <Select label={t('catalog:type')} name="type" defaultValue={typeOptions?.[0]?.value} options={typeOptions} required onChange={handleTypeChange} />
              </Box>
              <Box py={1}>
                <Select label={t('catalog:language')} name="language" defaultValue={currentCountry} options={typeLanguages.map(lang => ({label: lang.name, value: lang.id}))} required />
              </Box>
              <Box py={1}>
                <FormControl>
                  <FormLabel id="prices">{t('catalog:withPrices')}</FormLabel>
                  <RadioGroup
                    aria-labelledby="prices"
                    defaultValue="1"
                    name="prices"
                    required
                    row
                  >
                    <FormControlLabel value="1" control={<Radio color="primary" />} label={t('common:yes')} />
                    <FormControlLabel value="0" control={<Radio color="primary" />} label={t('common:no')} />
                  </RadioGroup>
                </FormControl>
              </Box>
              <Box py={1}>
                <FormControl>
                  <FormLabel id="target">{t('catalog:target')}</FormLabel>
                  <RadioGroup
                    aria-labelledby="target"
                    defaultValue="web"
                    name="target"
                    required
                    row
                  >
                    <FormControlLabel value="web" control={<Radio color="primary" />} label={t('catalog:web')} />
                    <FormControlLabel value="print" control={<Radio color="primary" />} label={t('catalog:print')} />
                  </RadioGroup>
                </FormControl>
              </Box>
              <Box py={1}>
                <Button type="submit" variant="contained" color="primary" disabled={status === 'processing'}>{t('catalog:generate')}</Button>
              </Box>
            </form>

            <Dialog onClose={handleDialogClose} aria-labelledby="catalog-dialog-title" open={dialogOpen}>
              <DialogTitle disableTypography>
                <Typography variant="h6" id="catalog-dialog-title">
                  {status === 'processing' && t('catalog:generating')}
                  {status === 'complete' && t('catalog:generationComplete')}
                  {status === 'error' && t('catalog:generationError')}
                </Typography>
              </DialogTitle>
              <DialogContent dividers>
                <Box py={1}>
                  {status === 'processing' && (
                    <LinearProgress variant={progress === null ? 'indeterminate' : 'determinate'} color="primary" value={progress} />
                  )}
                  {status === 'complete' && (
                    <Button variant="contained" color="primary" component="a" href={file} target="_blank" rel="noreferrer">{t('catalog:openFile')}</Button>
                  )}
                  {status === 'error' && (
                    <Alert severity="error">{errorMessage}</Alert>
                  )}
                </Box>
              </DialogContent>
              <DialogActions>
                <Button color="primary" onClick={handleDialogClose}>
                  {t(status === 'processing' ? 'common:cancel' : 'common:close')}
                </Button>
              </DialogActions>
            </Dialog>
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

export default CatalogPage;
